/**
 * Canonical settings row: title and description on the left, the control
 * right-aligned (DESIGN.md "Setting rows, not forms"). Rows commit on their
 * own — toggles/selects on change, text inputs on blur — and failures render
 * inline on the row, never at the page top.
 *
 * A row's extra explanation hangs off the row TITLE, exactly as everywhere
 * else in the app — there is no separate info glyph to find (the old `InfoTip`
 * is gone). The tooltip text obeys the one-sentence rule in DESIGN.md.
 */
import { Toggle } from "./Toggle";
import { Tooltip } from "./Tooltip";
import { useSettingCommit } from "../../lib/use-setting-commit";
import { blurOnEnter } from "../../lib/use-action";
import { InlineBanner } from "./InlineBanner";

import styles from "./SettingRow.module.css";

export function SettingRow({
  title,
  description,
  tooltip,
  htmlFor,
  labelId,
  optional = false,
  error,
  indent = false,
  children,
}: {
  title: string;
  description?: string;
  /** One sentence explaining what the setting affects; hangs off the title. */
  tooltip?: string;
  htmlFor?: string;
  labelId?: string;
  optional?: boolean;
  error?: string | null;
  /**
   * Dependent setting: indented under its parent row, and `2` for one
   * that depends on a dependent setting.
   */
  indent?: boolean | 2;
  children: React.ReactNode;
}) {
  const optionalMark = optional ? (
    <span className={styles.optional}> (optional)</span>
  ) : null;
  // The title itself is the tooltip trigger — no icon stands in for it. The
  // Tooltip wraps the title element rather than sitting inside it: Astryx keeps
  // the (closed) bubble in the DOM, and inside a <label> its text would join
  // the control's accessible name.
  const titleEl = htmlFor ? (
    <label id={labelId} htmlFor={htmlFor} className={styles.title}>
      {title}
      {optionalMark}
    </label>
  ) : (
    // A span, not a <p>: Astryx renders the tooltip bubble as a div in place,
    // which would close a <p> early.
    <span id={labelId} className={styles.titleBlock}>
      {title}
      {optionalMark}
    </span>
  );
  return (
    <div
      className={`${styles.row} ${
        indent === 2 ? styles.indent2 : indent ? styles.indent : ""
      }`}
    >
      <div className={styles.main}>
        {/* The trigger is inline-block around a block title — see
            `.tooltipTrigger`. */}
        {tooltip ? (
          <Tooltip content={tooltip} className={styles.tooltipTrigger}>
            {titleEl}
          </Tooltip>
        ) : (
          titleEl
        )}
        {description && <p className={styles.description}>{description}</p>}
        {error && <InlineBanner variant="error">{error}</InlineBanner>}
      </div>
      <div className={styles.control}>{children}</div>
    </div>
  );
}

/**
 * Text setting committed on blur (or Enter). An unchanged or — for required
 * rows — emptied value never fires a save; required rows revert instead.
 */
export function TextSettingRow({
  id,
  title,
  description,
  tooltip,
  value,
  onSave,
  type = "text",
  optional = false,
  required = false,
  indent = false,
  controlWidth = "default",
}: {
  id: string;
  title: string;
  description?: string;
  tooltip?: string;
  value: string;
  onSave: (next: string) => Promise<void>;
  type?: string;
  optional?: boolean;
  required?: boolean;
  indent?: boolean;
  /**
   * The field's width from `sm` up. Two widths and no third — the ordinary
   * one, or the narrow one for a number. Named rather than a class string:
   * a caller handing a component its geometry as text is how a look ends up
   * with no one place it is written.
   */
  controlWidth?: "default" | "narrow";
}) {
  const { draft, setDraft, onFocus, saving, error, commitDraft, resetDraft } =
    useSettingCommit(value, onSave);

  const commit = () => {
    // Required rows revert an emptied value instead of saving it.
    if (required && draft.trim() === "") return resetDraft();
    void commitDraft();
  };

  return (
    <SettingRow
      title={title}
      description={description}
      tooltip={tooltip}
      htmlFor={id}
      optional={optional}
      error={error}
      indent={indent}
    >
      <input
        id={id}
        type={type}
        className={
          controlWidth === "narrow" ? styles.fieldNarrow : styles.field
        }
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={onFocus}
        onBlur={commit}
        onKeyDown={blurOnEnter}
      />
    </SettingRow>
  );
}

/**
 * Number setting, committed on blur (or Enter) like a text row. Emptied,
 * it goes back to what is stored; a value outside the bounds stays in the
 * field with the bounds said under it, and is not sent. The two number
 * rows there were had a policy each: the ring time said what was wrong,
 * the estimate rows quietly saved a nonsense value as 0.
 */
export function NumberSettingRow({
  id,
  title,
  description,
  tooltip,
  value,
  onSave,
  min,
  max,
  integer = false,
  unit,
  indent = false,
}: {
  id: string;
  title: string;
  description?: string;
  tooltip?: string;
  value: number;
  onSave: (next: number) => Promise<void>;
  min: number;
  max?: number;
  /** Whole numbers only. */
  integer?: boolean;
  /** Drawn after the field and named in the bounds sentence: "seconds". */
  unit?: string;
  indent?: boolean;
}) {
  const {
    draft,
    setDraft,
    onFocus,
    saving,
    error,
    setError,
    commitDraft,
    resetDraft,
  } = useSettingCommit(String(value), (next) => onSave(Number(next)));

  const kind = `${integer ? "a whole number" : "a number"}${unit ? ` of ${unit}` : ""}`;
  const bounds =
    max === undefined
      ? `Enter ${kind} of ${min} or more.`
      : `Enter ${kind} from ${min} to ${max}.`;

  const commit = () => {
    setError(null);
    if (draft.trim() === "") return resetDraft();
    const n = Number(draft);
    if (
      !Number.isFinite(n) ||
      (integer && !Number.isInteger(n)) ||
      n < min ||
      (max !== undefined && n > max)
    ) {
      setError(bounds);
      return;
    }
    void commitDraft();
  };

  return (
    <SettingRow
      title={title}
      description={description}
      tooltip={tooltip}
      htmlFor={id}
      error={error}
      indent={indent}
    >
      <span className={styles.numberField}>
        <input
          id={id}
          type="number"
          inputMode={integer ? "numeric" : "decimal"}
          min={min}
          max={max}
          step={integer ? 1 : "any"}
          className={styles.fieldNarrow}
          value={draft}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={onFocus}
          onBlur={commit}
          onKeyDown={blurOnEnter}
        />
        {unit && <span className={styles.unit}>{unit}</span>}
      </span>
    </SettingRow>
  );
}

/**
 * Toggle setting: flipping IS the commit; on failure the row shows why.
 *
 * `disabled` is for a setting that exists but cannot be offered here —
 * the switch stays visible and off, because a row that is simply absent
 * gives the reader nowhere to put the reason. Callers that disable a row
 * say why in its `description`, and may hang one sentence more off the
 * greyed switch itself (`disabledTooltip`): what the switch reads and what
 * connecting would make of it, shown when the reader hovers the thing they
 * cannot flip.
 */
export function ToggleSettingRow({
  id,
  title,
  description,
  tooltip,
  disabledTooltip,
  checked,
  onChange,
  disabled = false,
  indent = false,
  label = title,
}: {
  id: string;
  title: string;
  description?: string;
  tooltip?: string;
  /** On the switch, only while it is disabled. */
  disabledTooltip?: string;
  checked: boolean;
  onChange: (next: boolean) => Promise<void>;
  disabled?: boolean;
  /** Dependent setting: indented under its parent row. */
  indent?: boolean;
  /**
   * The switch's accessible name, where the title alone is ambiguous —
   * one row per form, each titled the same. Defaults to the title.
   */
  label?: string;
}) {
  const { saving, error, commit } = useSettingCommit(checked, onChange);

  return (
    <SettingRow
      title={title}
      description={description}
      tooltip={tooltip}
      labelId={id}
      error={error}
      indent={indent}
    >
      {/* aria-label, not aria-labelledby: the Astryx Switch doesn't forward
          labelledby to its input, which left every switch named "Toggle". */}
      {disabled && disabledTooltip ? (
        <Tooltip content={disabledTooltip} trigger="control">
          <Toggle
            checked={checked}
            disabled
            onChange={(next) => void commit(next)}
            aria-label={label}
          />
        </Tooltip>
      ) : (
        <Toggle
          checked={checked}
          disabled={saving || disabled}
          onChange={(next) => void commit(next)}
          aria-label={label}
        />
      )}
    </SettingRow>
  );
}
